<template>
  <div class="d-flex flex-column">
    <div v-if="!props.reduced" v class="grid-header">
      <h1>{{$t("rappels_mensuales.titulo")}}</h1>
      <div class="toolbar">
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" 
      ref="grid" class="dx-card wide-card grid-rappels-mensuales"
      @selectionChanged="handleSelectionChanged">
      <DxColumn :visible="!props.filtroPendientes" data-field="Facturado" :caption="$t('rappels_mensuales.grid.liquidado')"
        cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxSelection
        v-if="props.filasSeleccionables"
        select-all-mode="allPages"
        show-check-boxes-mode="always"
        mode="multiple"
        :selectedRowKeys="rappelsSeleccionados"
      />
      <DxColumn data-field="Anyo" :caption="$t('rappels_mensuales.grid.anyo')" data-type="number"></DxColumn>
      <DxColumn data-field="Mes" :caption="$t('rappels_mensuales.grid.mes')" cell-template="mesTemplate">
        <DxLookup v-bind="getLookup('rappels_mensuales', 'mes')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="EmpresaServicios" :caption="$t('rappels_mensuales.grid.empresa_servicios')"></DxColumn>
      <DxColumn data-field="TipoElemento" :caption="$t('rappels_mensuales.grid.tipo_elemento')">
        <DxLookup column-auto-width="true" v-bind="getLookup('rappels_mensuales', 'tipo_elemento')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="TipoSuministro" :caption="$t('rappels_mensuales.grid.tipo_suministro')">
        <DxLookup column-auto-width="true" v-bind="getLookup('rappels_mensuales', 'tipo_suministro')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="ComisionTotal" :caption="$t('rappels_mensuales.grid.comision_total')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="NumOperaciones" :caption="$t('rappels_mensuales.grid.num_operaciones')" data-type="number"></DxColumn>
      <DxColumn :visible="!props.reduced" data-field="ComisionPorOperacion" :caption="$t('rappels_mensuales.grid.comision_por_operacion')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn :visible="!props.reduced" data-field="PotenciaMax" :caption="$t('rappels_mensuales.grid.potencia_max')" data-type="number"
        cell-template="potenciaTemplate">
      </DxColumn>
      <DxColumn type="buttons" :visible="!props.reduced" :caption="$t('rappels_mensuales.grid.acciones')" :fixed="true" fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          icon: 'fa-solid fa-magnifying-glass text-success',
          hint: $t('rappels_mensuales.grid.ver_detalle'),
          onClick: (e: any) => verDetalle(e)
        }
      ]"/>
      <DxColumnFixing :enabled="true" />
    
      <template #mesTemplate="{ data }">
        <p class="text-center mb-0">
          {{ $t(`rappels_mensuales.mes.${data.value}`) }}
        </p>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #potenciaTemplate="{ data }">
        <span v-if="data.value">{{ data.value }} kW</span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxColumn,
  DxDataGrid,
  DxFormat,
  DxLookup,
  DxColumnFixing,
  DxSelection
} from "devextreme-vue/data-grid";
import {
  currencyFormat,
  getDefaultGridProperties,
  getLookup, getColumnLookupBoolean} from "@helpers/grid-helper";
import { useModalService } from "@services/modal-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref } from "vue";
import type DevExpress from "devextreme";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import RappelMensualDetail from "@/components/liquidaciones/RappelMensualDetail.vue";
import { type DXLoadOptions } from "@/.shared/.api";

const props = defineProps({
  filtroPendientes: { type: Boolean, default: false },
  reduced: { type: Boolean, default: false },
  filasSeleccionables: { type: Boolean, default: false },
  seleccionarTodas: { type: Boolean, default: false }
})

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();
const intl = useIntl();
const rappelsSeleccionados = ref<Array<object>>([]);
const filtroPendientes = ref<boolean>(props.filtroPendientes); 
const emit = defineEmits(['actualizarRappels']);
let inicializado = false;

const gridProperties: Partial<DxDataGrid> = {
    ...getDefaultGridProperties(),
    remoteOperations: false,
    paging: {
      pageSize: 4,
    },
    pager: {
      showPageSizeSelector: false,
    }
}

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async(p) => {

      // Se preseleccionan todas las opciones en la carga inicial
      if (!inicializado && props.seleccionarTodas) {
        grid.value?.instance?.selectAll();
        inicializado = true;
      }

      const q = p as DXLoadOptions;
      if (filtroPendientes.value) 
      {
        if (q.filter != null && q.filter.length >= 0)
          q.filter = [["Facturado", "=", "false"], "and", q.filter];
        else
          q.filter = ["Facturado", "=", "false"];
      }
      return await api.callStandardApi("getRappelsMensualesEnergiaForGrid", { showLoading: false }, q)
    }
  
  });

});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let title = `${intl.i18n.t("rappels_mensuales.detalle.titulo")}`;
  await modal.showModal(RappelMensualDetail, { IdRappelMensual: data.row?.data.Id }, { title: title });
}

const handleSelectionChanged = (e: any) => {
  const selectedRows = e.selectedRowsData;
  rappelsSeleccionados.value = selectedRows.map((row: any) => row);
  emit('actualizarRappels', rappelsSeleccionados.value);
};

const refreshGrid = () => {
  grid.value?.instance?.refresh();
}

defineExpose({refreshGrid});
</script>

<style lang="scss" scoped>


.lupa {
  color: var(--text-secondary-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}
</style>
